import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { GitCompareArrows, Sparkles, Loader2, Gauge, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { streamChat } from "@/lib/gateway";

function Panel({ side, models, modelId, setModelId, state }) {
  return (
    <div className="flex min-h-[420px] flex-col rounded-2xl border border-gold-500/25 bg-card" data-testid={`compare-panel-${side}`}>
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border p-3">
        {models.map((m) => (
          <button key={m.id} onClick={() => setModelId(m.id)} data-testid={`compare-${side}-model-${m.id}`}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${modelId === m.id ? "border-gold-500 bg-gold-500/10 text-gold-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {m.label} <span className="font-mono opacity-70">{m.cost}cr</span>
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto whitespace-pre-wrap p-4 text-sm leading-relaxed" style={{ maxHeight: "360px" }} data-testid={`compare-output-${side}`}>
        {state.error
          ? <span className="text-red-300">{state.error}</span>
          : state.text || (!state.running && <span className="text-muted-foreground">La réponse s'affichera ici.</span>)}
        {state.running && <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-gold-400" />}
      </div>
      <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Gauge className="h-3.5 w-3.5" />{state.latency != null ? `${state.latency} ms` : "—"}</span>
        <span className="flex items-center gap-1 font-mono text-gold-400"><Coins className="h-3.5 w-3.5" />{models.find((m) => m.id === modelId)?.cost ?? 1} cr.</span>
      </div>
    </div>
  );
}

export default function Compare() {
  const { refreshCredits } = useOutletContext();
  const [models, setModels] = useState([]);
  const [left, setLeft] = useState("crerea-pro");
  const [right, setRight] = useState("crerea-gpt");
  const [prompt, setPrompt] = useState("");
  const [running, setRunning] = useState(false);
  const [ls, setLs] = useState({ text: "", latency: null, running: false, error: "" });
  const [rs, setRs] = useState({ text: "", latency: null, running: false, error: "" });

  useEffect(() => { api.get("/models").then((r) => setModels(r.data.filter((m) => m.type === "text"))).catch(() => {}); }, []);

  const runOne = async (modelId, setState) => {
    setState({ text: "", latency: null, running: true, error: "" });
    const t0 = performance.now();
    let acc = "";
    try {
      await streamChat({ model: modelId, messages: [{ role: "user", content: prompt }], onDelta: (d) => { acc += d; setState((s) => ({ ...s, text: acc })); } });
      setState((s) => ({ ...s, running: false, latency: Math.round(performance.now() - t0) }));
    } catch (e) {
      const msg = e.status === 402 ? "Crédits insuffisants." : (e.message || "Échec");
      setState((s) => ({ ...s, running: false, error: msg }));
    }
  };

  const run = async () => {
    if (!prompt.trim() || running) return;
    setRunning(true);
    await Promise.all([runOne(left, setLs), runOne(right, setRs)]);
    refreshCredits?.();
    setRunning(false);
  };

  return (
    <div className="space-y-6" data-testid="compare-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-400">CRÉRÉA · Comparateur</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">Deux modèles, un seul prompt</h1>
        <p className="mt-2 text-sm text-muted-foreground">Lancez la même requête en parallèle et comparez réponse, vitesse et coût.</p>
      </div>

      <div className="rounded-2xl border border-gold-500/25 bg-card p-4">
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="compare-prompt" rows={3}
          placeholder="Écrivez le prompt à comparer sur les deux modèles…" className="w-full resize-none rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm outline-none focus:border-gold-500/60" />
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Coût total : <span className="font-mono text-gold-400">{(models.find((m) => m.id === left)?.cost ?? 1) + (models.find((m) => m.id === right)?.cost ?? 1)} crédits</span></span>
          <Button onClick={run} disabled={running || !prompt.trim()} data-testid="compare-run-btn" className="rounded-full bg-gold-500 text-black hover:bg-gold-400">
            {running ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Comparaison…</> : <><GitCompareArrows className="mr-1 h-4 w-4" />Comparer</>}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel side="left" models={models} modelId={left} setModelId={setLeft} state={ls} />
        <Panel side="right" models={models} modelId={right} setModelId={setRight} state={rs} />
      </div>
    </div>
  );
}
